import { useState } from "react";
import { Link } from "react-router-dom";
import { FacebookLogo, InstagramLogo, YoutubeLogo, TwitterLogo, MapPin, Phone, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { CHURCH } from "@/lib/church";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const res = await api.post("/newsletter", { email });
            toast.success(res.data.already ? "You're already subscribed." : "Thanks for subscribing!");
            setEmail("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="bg-[#4C1D95] text-white">
            {/* Newsletter CTA strip */}
            <div className="bg-[#4C1D95] py-10 lg:py-14 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-5">
                        <p className="overline text-[#FFC72C] mb-2">Stay Connected</p>
                        <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tighter leading-tight">Get church updates straight to your inbox.</h3>
                    </div>
                    <form onSubmit={subscribe} className="lg:col-span-7 flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            data-testid="newsletter-email-input"
                            className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/60 px-5 py-4 text-sm focus:outline-none focus:border-[#FFC72C] focus:bg-white/15"
                        />
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="newsletter-submit-button"
                            className="inline-flex items-center justify-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-7 py-4 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
                        >
                            <PaperPlaneTilt size={14} weight="bold" /> {submitting ? "Subscribing..." : "Subscribe"}
                        </button>
                    </form>
                </div>
            </div>

            {/* Main footer */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                <div>
                    <div className="flex items-center gap-3 mb-5">
                        <span className="w-12 h-12 bg-[#FFC72C] flex items-center justify-center font-black text-[#4C1D95] text-xl tracking-tighter">GM</span>
                        <span>
                            <span className="block text-base font-black tracking-tight leading-tight">GREATER MOUNT OLIVE</span>
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-[#FFC72C] font-bold">A.M.E. Church</span>
                        </span>
                    </div>
                    <p className="text-sm text-white/70 leading-relaxed">A joyful, Spirit-filled community in West Philadelphia rooted in the African Methodist Episcopal tradition.</p>
                </div>

                <div>
                    <h4 className="overline text-[#FFC72C] mb-5">Visit</h4>
                    <ul className="space-y-3 text-sm text-white/80">
                        <li className="flex gap-3"><MapPin size={18} weight="bold" className="text-[#FFC72C] shrink-0 mt-0.5" /><span>{CHURCH.address}</span></li>
                        <li className="flex gap-3"><Phone size={18} weight="bold" className="text-[#FFC72C] shrink-0 mt-0.5" /><a href={CHURCH.phoneHref} className="hover:text-white" data-testid="footer-phone">{CHURCH.phone}</a></li>
                        <li className="flex gap-3"><EnvelopeSimple size={18} weight="bold" className="text-[#FFC72C] shrink-0 mt-0.5" /><a href={CHURCH.emailHref} className="hover:text-white" data-testid="footer-email">{CHURCH.email}</a></li>
                    </ul>
                </div>

                <div>
                    <h4 className="overline text-[#FFC72C] mb-5">Service Times</h4>
                    <ul className="space-y-3 text-sm">
                        {CHURCH.services.map((s) => (
                            <li key={s.label} className="flex justify-between gap-4 border-b border-white/10 pb-2">
                                <span className="text-white/80">{s.label}</span>
                                <span className="text-[#FFC72C] font-bold">{s.time}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h4 className="overline text-[#FFC72C] mb-5">Follow</h4>
                    <div className="flex gap-3 mb-6">
                        <a href={CHURCH.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="w-11 h-11 bg-white/10 hover:bg-[#FFC72C] hover:text-[#2E1A47] flex items-center justify-center transition-colors"><FacebookLogo size={18} weight="bold" /></a>
                        <a href={CHURCH.social.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="w-11 h-11 bg-white/10 hover:bg-[#FFC72C] hover:text-[#2E1A47] flex items-center justify-center transition-colors"><InstagramLogo size={18} weight="bold" /></a>
                        <a href={CHURCH.social.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" aria-label="YouTube" className="w-11 h-11 bg-white/10 hover:bg-[#FFC72C] hover:text-[#2E1A47] flex items-center justify-center transition-colors"><YoutubeLogo size={18} weight="bold" /></a>
                        <a href={CHURCH.social.twitter} target="_blank" rel="noopener noreferrer" data-testid="social-twitter" aria-label="Twitter" className="w-11 h-11 bg-white/10 hover:bg-[#FFC72C] hover:text-[#2E1A47] flex items-center justify-center transition-colors"><TwitterLogo size={18} weight="bold" /></a>
                    </div>
                    <p className="text-xs text-white/60 leading-relaxed">Tap to join us across social. We'd love to connect.</p>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
                    <p>© {new Date().getFullYear()} Greater Mount Olive AME Church. All rights reserved.</p>
                    <div className="flex gap-6 uppercase tracking-widest font-bold">
                        <Link to="/friends" className="hover:text-[#FFC72C]" data-testid="footer-friends-link">Friends</Link>
                        <Link to="/give" className="hover:text-[#FFC72C]" data-testid="footer-give-link">Give</Link>
                        <Link to="/give#tribute-gifts" className="hover:text-[#FFC72C]" data-testid="footer-tribute-link">Tribute</Link>
                        <Link to="/contact" className="hover:text-[#FFC72C]" data-testid="footer-contact-link">Contact</Link>
                        <Link to="/admin/login" className="hover:text-[#FFC72C]" data-testid="footer-admin-link">Admin</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
